// src/pages/ClientNewPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form, Button, Alert, Row, Col, Card, Modal } from "react-bootstrap";
import api from "../api";
import HouseholdMembersSection from "../components/HouseholdMembersSection";

// Helpers
const todayISO = () => new Date().toISOString().slice(0, 10);

const REFERRED_BY_OPTIONS = [
  "Church",
  "CMH",
  "DHHS",
  "Facebook",
  "MMCAA",
  "United Way",
  "Salvation Army",
  "Friend/Family",
  "Other Agency",
];

const COUNTY_OPTIONS = [
  "Clare",
  "Gladwin",
  "Isabella",
  "Mecosta",
  "Midland",
  "Missaukee",
  "Osceola",
];

// Best-effort label extraction for housing types
const getHousingTypeLabel = (ht) => ht?.name || ht?.label || ht?.description || "";

const EMPTY_LANDLORD_FORM = {
  id: null,
  name: "",
  phone: "",
  address: "",
  city: "",
  state: "MI",
  zipcode: "",
};

const INITIAL_FORM = {
  intake_date: todayISO(),
  intake_status: "",

  first_name: "",
  middle_name: "",
  last_name: "",
  birth_date: "",
  ssn: "",

  phone_primary: "",
  phone_secondary: "",
  // No Phone satisfies the phone requirement for partial intake
  no_phone: false,

  // Unsheltered satisfies address requirement for partial intake
  unsheltered: false,

  address_line1: "",
  address_line2: "",
  city: "",
  state: "MI",
  postal_code: "",
  county: "Clare",

  // housing_type is an integer in clients table; store as "" in UI until chosen
  housing_type: "",

  // landlord linkage (nullable int)
  landlord_id: "",

  how_can_help_today: "",
  how_heard: "",
  referred_by: "",

  lives_in_clare: false,
  attends_church_in_clare: false,
  children_school_in_clare: false,
  permission_to_verify: false,
};

export default function ClientNewPage() {
  const navigate = useNavigate();

  const [form, setForm] = useState(INITIAL_FORM);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [householdMembers, setHouseholdMembers] = useState([]);
  const [showConsent, setShowConsent] = useState(true);

  // NEW: once partial is saved, we will have a clientId and we can unlock the complete intake fields
  const [clientId, setClientId] = useState(null);

  // Housing types
  const [housingTypes, setHousingTypes] = useState([]);
  const [housingTypesLoading, setHousingTypesLoading] = useState(false);

  // Landlord lookup + add/edit
  const [landlords, setLandlords] = useState([]);
  const [landlordsLoading, setLandlordsLoading] = useState(false);

  // What user sees/types in the landlord field
  const [landlordQuery, setLandlordQuery] = useState("");

  // Modal state for add/edit landlord
  const [showLandlordModal, setShowLandlordModal] = useState(false);
  const [landlordModalMode, setLandlordModalMode] = useState("add"); // "add" | "edit"
  const [landlordForm, setLandlordForm] = useState(EMPTY_LANDLORD_FORM);
  const [landlordModalSaving, setLandlordModalSaving] = useState(false);
  const [landlordModalError, setLandlordModalError] = useState("");

  // Load housing types on mount
  useEffect(() => {
    const loadHousingTypes = async () => {
      setHousingTypesLoading(true);
      try {
        const res = await api.get("/clients/housing-types");
        const list = Array.isArray(res.data) ? res.data : res.data.items || [];
        setHousingTypes(list);
      } catch (e) {
        setHousingTypes([]);
      } finally {
        setHousingTypesLoading(false);
      }
    };
    loadHousingTypes();
  }, []);

  const fetchLandlords = async (q = "") => {
    setLandlordsLoading(true);
    try {
      const res = await api.get("/clients/landlords", {
        params: q ? { q } : {},
      });
      const list = Array.isArray(res.data) ? res.data : res.data.items || [];
      setLandlords(list);
    } catch (e) {
      setLandlords([]);
    } finally {
      setLandlordsLoading(false);
    }
  };

  // Initial landlord list load
  useEffect(() => {
    fetchLandlords("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Protect against browser close / refresh with unsaved changes
  useEffect(() => {
    const beforeUnload = (e) => {
      if (!dirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [dirty]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setSuccess("");
  };

  const handleCheckbox = (field) => (e) => {
    const value = e.target.checked;
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    setSuccess("");
  };

  // Determine if landlord field should show based on selected housing type label
  const shouldShowLandlord = useMemo(() => {
    const idVal = form?.housing_type;
    if (idVal == null || idVal === "") return false;

    const ht = housingTypes.find((x) => String(x.id) === String(idVal));
    const label = (getHousingTypeLabel(ht) || "").toLowerCase();

    return label === "rent" || label === "rent - multi-tenant";
  }, [form?.housing_type, housingTypes]);

  // Clear landlord when housing type changes away from rent
  useEffect(() => {
    if (!shouldShowLandlord) {
      if (form.landlord_id || landlordQuery) {
        setForm((prev) => ({ ...prev, landlord_id: "" }));
        setLandlordQuery("");
        setDirty(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowLandlord]);

  // Find the INT housing_type id for "Unsheltered" using same label logic
  const unshelteredHousingTypeId = useMemo(() => {
    if (!Array.isArray(housingTypes) || housingTypes.length === 0) return null;
    const match = housingTypes.find((ht) => {
      const label = (getHousingTypeLabel(ht) || "").trim().toLowerCase();
      // Use includes to tolerate variants like "Unsheltered (no fixed address)"
      return label.includes("unsheltered");
    });
    return match ? Number(match.id) : null;
  }, [housingTypes]);

  const selectedLandlord = useMemo(() => {
    const lid = form?.landlord_id;
    if (!lid) return null;
    return landlords.find((l) => String(l.id) === String(lid)) || null;
  }, [form?.landlord_id, landlords]);

  const exactMatchLandlordByName = useMemo(() => {
    const q = (landlordQuery || "").trim().toLowerCase();
    if (!q) return null;
    return landlords.find((l) => (l.name || "").trim().toLowerCase() === q) || null;
  }, [landlordQuery, landlords]);

  const landlordTextChanged = async (value) => {
    setLandlordQuery(value);
    setDirty(true);
    setSuccess("");

    // Clear selection until a known match is selected
    setForm((prev) => ({ ...prev, landlord_id: "" }));

    // Server-side search to populate suggestions
    await fetchLandlords(value);
  };

  const handleLandlordBlurOrPick = (value) => {
    const q = (value || "").trim();
    setLandlordQuery(q);
    setDirty(true);
    setSuccess("");

    const found =
      landlords.find((l) => (l.name || "").trim().toLowerCase() === q.toLowerCase()) || null;

    setForm((prev) => ({ ...prev, landlord_id: found ? found.id : "" }));
  };

  const openAddLandlord = () => {
    setLandlordModalError("");
    setLandlordModalMode("add");
    setLandlordForm({
      ...EMPTY_LANDLORD_FORM,
      name: (landlordQuery || "").trim(),
    });
    setShowLandlordModal(true);
  };

  const openEditLandlord = () => {
    if (!selectedLandlord) return;
    setLandlordModalError("");
    setLandlordModalMode("edit");
    setLandlordForm({
      id: selectedLandlord.id,
      name: selectedLandlord.name || "",
      phone: selectedLandlord.phone || "",
      address: selectedLandlord.address || "",
      city: selectedLandlord.city || "",
      state: selectedLandlord.state || "MI",
      zipcode: selectedLandlord.zipcode || "",
    });
    setShowLandlordModal(true);
  };

  const saveLandlordFromModal = async () => {
    setLandlordModalSaving(true);
    setLandlordModalError("");

    const payload = {
      name: (landlordForm.name || "").trim(),
      phone: (landlordForm.phone || "").trim() || null,
      address: (landlordForm.address || "").trim() || null,
      city: (landlordForm.city || "").trim() || null,
      state: (landlordForm.state || "").trim() || null,
      zipcode: (landlordForm.zipcode || "").trim() || null,
    };

    if (!payload.name) {
      setLandlordModalError("Landlord name is required.");
      setLandlordModalSaving(false);
      return;
    }

    try {
      let res;
      if (landlordModalMode === "add") {
        res = await api.post("/clients/landlords", payload);
      } else {
        if (!landlordForm.id) {
          setLandlordModalError("Missing landlord id.");
          setLandlordModalSaving(false);
          return;
        }
        res = await api.patch(`/clients/landlords/${landlordForm.id}`, payload);
      }

      const saved = res.data;

      // Refresh list and select the saved landlord
      await fetchLandlords("");

      setLandlordQuery(saved.name || payload.name);
      setForm((prev) => ({ ...prev, landlord_id: saved.id }));
      setDirty(true);

      setShowLandlordModal(false);
    } catch (e) {
      setLandlordModalError(
        landlordModalMode === "add" ? "Failed to create landlord." : "Failed to update landlord."
      );
    } finally {
      setLandlordModalSaving(false);
    }
  };

  // ----------------------------
  // Partial Intake readiness (controls Do Complete Intake enabled state)
  // ----------------------------
  const isPartialIntakeReady = useMemo(() => {
    const first = (form.first_name || "").trim();
    const last = (form.last_name || "").trim();
    const birth = (form.birth_date || "").trim();

    const phone1 = (form.phone_primary || "").trim();
    const phone2 = (form.phone_secondary || "").trim();
    const hasPhone = !!(phone1 || phone2) || !!form.no_phone;

    const isUnsheltered = !!form.unsheltered;

    const addr1 = (form.address_line1 || "").trim();
    const city = (form.city || "").trim();
    const state = (form.state || "").trim();
    const zip = (form.postal_code || "").trim();
    const hasFullAddress = !!(addr1 && city && state && zip);

    const addressSatisfied = isUnsheltered || hasFullAddress;

    return !!(first && last && birth && hasPhone && addressSatisfied);
  }, [form]);

  // Auto-set status once ready (do not auto-clear)
  useEffect(() => {
    if (!isPartialIntakeReady) return;
    setForm((prev) => {
      if (prev.intake_status === "Partial Intake") return prev;
      return { ...prev, intake_status: "Partial Intake" };
    });
  }, [isPartialIntakeReady]);

  // When "No Phone" is checked, clear the phone fields to avoid inconsistent data
  useEffect(() => {
    if (!form.no_phone) return;
    setForm((prev) => ({ ...prev, phone_primary: "", phone_secondary: "" }));
    setDirty(true);
    setSuccess("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.no_phone]);

  // When unsheltered is checked, clear address fields to avoid inconsistent data
  useEffect(() => {
    if (!form.unsheltered) return;
    setForm((prev) => ({
      ...prev,
      address_line1: "",
      address_line2: "",
      city: "",
      state: prev.state || "MI",
      postal_code: "",
    }));
    setDirty(true);
    setSuccess("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.unsheltered]);

  // Disable address fields if unsheltered
  const addressDisabled = !!form.unsheltered;

  // ----------------------------
  // SAVE / CREATE
  // ----------------------------
  const buildPayload = (overrides = {}) => {
    const isUnsheltered = !!form.unsheltered;

    // Normal dropdown value -> int/null
    const uiHousingType =
      form.housing_type === "" || form.housing_type == null ? null : Number(form.housing_type);

    // If unsheltered is checked, override with the "Unsheltered" id (int)
    const housingTypeFinal = isUnsheltered ? unshelteredHousingTypeId : uiHousingType;

    return {
      ...form,
      ...overrides,
      intake_date: form.intake_date || null,
      birth_date: form.birth_date || null,

      // INT FK
      housing_type: housingTypeFinal,

      landlord_id:
        !isUnsheltered && shouldShowLandlord
          ? form.landlord_id === "" || form.landlord_id == null
            ? null
            : Number(form.landlord_id)
          : null,
    };
  };

  const ensureUnshelteredHousingTypeExists = () => {
    if (!form.unsheltered) return true;
    if (unshelteredHousingTypeId) return true;
    setError("Unsheltered is checked, but no Housing Type matching 'Unsheltered' exists.");
    return false;
  };

  // Do Complete Intake:
  // - save current fields with intake_status='Partial Intake'
  // - then navigate to EditClient page for that client
  const handleDoCompleteIntake = async () => {
    setError("");
    setSuccess("");

    if (!isPartialIntakeReady) {
      setError(
        "To start the complete intake, please complete the required Partial Intake fields (name, birth date, phone, and address or Unsheltered)."
      );
      return;
    }

    if (!ensureUnshelteredHousingTypeExists()) return;

    setSaving(true);
    try {
      // If already created, save partial then go to edit
      if (clientId) {
        const payload = buildPayload({ intake_status: "Partial Intake" });
        await api.patch(`/clients/${clientId}`, payload);
        setDirty(false);
        navigate(`/clients/${clientId}`);
        return;
      }

      // Create with forced Partial Intake
      const payload = buildPayload({ intake_status: "Partial Intake" });
      const res = await api.post("/clients", payload);
      const newId = res.data?.id;

      if (!newId) {
        setError("Client was created but no id was returned.");
        return;
      }

      setDirty(false);
      navigate(`/clients/${newId}`);
    } catch (e) {
      setError("Failed to save Partial Intake.");
    } finally {
      setSaving(false);
    }
  };

  // Save:
  // - always save current fields with intake_status='Partial Intake' (create or update)
  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    if (!ensureUnshelteredHousingTypeExists()) {
      setSaving(false);
      return;
    }

    const payload = buildPayload({ intake_status: "Partial Intake" });

    try {
      if (!clientId) {
        // Create
        const res = await api.post("/clients", payload);
        const newId = res.data?.id;

        if (!newId) {
          setError("Client was created but no id was returned.");
          return;
        }

        setClientId(newId);

        // Persist household members if present (optional)
        const items = (householdMembers || [])
          .map((m) => ({
            full_name: (m.full_name || "").trim(),
            relationship: (m.relationship || "").trim(),
            birth_date: m.birth_date || null,
            notes: m.notes || null,
          }))
          .filter((m) => m.full_name && m.relationship);

        if (items.length > 0) {
          await api.post(`/clients/${newId}/family-members/bulk`, { items });
        }

        setDirty(false);
        navigate("/clients");
      } else {
        // Update
        await api.patch(`/clients/${clientId}`, payload);

        const items = (householdMembers || [])
          .map((m) => ({
            full_name: (m.full_name || "").trim(),
            relationship: (m.relationship || "").trim(),
            birth_date: m.birth_date || null,
            notes: m.notes || null,
          }))
          .filter((m) => m.full_name && m.relationship);

        if (items.length > 0) {
          await api.post(`/clients/${clientId}/family-members/bulk`, { items });
        }

        setDirty(false);
        navigate("/clients");
      }
    } catch (err) {
      setError(clientId ? "Failed to save client" : "Failed to create client");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (dirty) {
      const confirmLeave = window.confirm("You have unsaved changes. Discard them and leave this page?");
      if (!confirmLeave) return;
    }
    navigate("/clients");
  };

  // Modal actions
  const handleConsentCancel = () => {
    navigate("/clients");
  };

  const handleConsentAccept = () => {
    setForm((prev) => ({ ...prev, permission_to_verify: true }));
    setDirty(true);
    setShowConsent(false);
  };

  const canOpenAddLandlord =
    shouldShowLandlord && !!(landlordQuery || "").trim() && !exactMatchLandlordByName;

  const canOpenEditLandlord = shouldShowLandlord && !!selectedLandlord;

  return (
    <>
      {/* Consent / Disclosure Modal */}
      <Modal show={showConsent} onHide={() => {}} backdrop="static" keyboard={false} centered size="lg">
        <Modal.Header>
          <Modal.Title>Consent to Verify Information</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Please read the following before creating a new client record.</p>

          <div className="border rounded p-3 bg-light">
            <p className="mb-2">
              1. We are not an emergency service. It will take a day or two to process your intake.
              <br />
              2. We do not give money directly.
              <br />
              3. We are a Christian clearinghouse that will be asking questions to try and match resources with needs.
              <br />
              4.{" "}
              <strong>Do we have your permission to verify the information you give us?</strong>
              <br />
              <br />
            </p>
            <p className="mb-0">
              If they do not consent, click <strong>Cancel</strong> to return to the client list.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleConsentCancel}>
            Cancel
          </Button>
          <Button onClick={handleConsentAccept}>Accept</Button>
        </Modal.Footer>
      </Modal>

      {/* Landlord Add/Edit Modal */}
      <Modal show={showLandlordModal} onHide={() => setShowLandlordModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{landlordModalMode === "add" ? "Add Landlord" : "Edit Landlord"}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {landlordModalError && <Alert variant="danger">{landlordModalError}</Alert>}

          <Row>
            <Col md={6} sm={12}>
              <Form.Group className="mb-3" controlId="landlordName">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={landlordForm.name}
                  onChange={(e) => setLandlordForm((p) => ({ ...p, name: e.target.value }))}
                />
              </Form.Group>
            </Col>

            <Col md={6} sm={12}>
              <Form.Group className="mb-3" controlId="landlordPhone">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="text"
                  value={landlordForm.phone}
                  onChange={(e) => setLandlordForm((p) => ({ ...p, phone: e.target.value }))}
                  placeholder="Optional"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={6} sm={12}>
              <Form.Group className="mb-3" controlId="landlordAddress">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  type="text"
                  value={landlordForm.address}
                  onChange={(e) => setLandlordForm((p) => ({ ...p, address: e.target.value }))}
                  placeholder="Optional"
                />
              </Form.Group>
            </Col>

            <Col md={6} sm={12}>
              <Form.Group className="mb-3" controlId="landlordCity">
                <Form.Label>City</Form.Label>
                <Form.Control
                  type="text"
                  value={landlordForm.city}
                  onChange={(e) => setLandlordForm((p) => ({ ...p, city: e.target.value }))}
                  placeholder="Optional"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row>
            <Col md={4} sm={12}>
              <Form.Group className="mb-3" controlId="landlordState">
                <Form.Label>State</Form.Label>
                <Form.Control
                  type="text"
                  value={landlordForm.state}
                  onChange={(e) => setLandlordForm((p) => ({ ...p, state: e.target.value }))}
                />
              </Form.Group>
            </Col>

            <Col md={4} sm={12}>
              <Form.Group className="mb-3" controlId="landlordZip">
                <Form.Label>Zipcode</Form.Label>
                <Form.Control
                  type="text"
                  value={landlordForm.zipcode}
                  onChange={(e) => setLandlordForm((p) => ({ ...p, zipcode: e.target.value }))}
                  placeholder="Optional"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLandlordModal(false)}>
            Cancel
          </Button>
          <Button onClick={saveLandlordFromModal} disabled={landlordModalSaving}>
            {landlordModalSaving ? "Saving..." : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <h3 className="mb-0">New Client Intake</h3>
          {form.intake_status === "Partial Intake" && (
            <span className="badge bg-info text-dark">Partial Intake</span>
          )}
          {clientId && <span className="badge bg-secondary">Client #{clientId}</span>}
        </div>

        <div>
          <Button variant="secondary" className="me-2" onClick={handleCancel}>
            Back to list
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : clientId ? "Save" : "Save (Create)"}
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card>
        <Card.Body>
          <Form>
            {/* Intake Date */}
            <Row className="align-items-end">
              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="intakeDate">
                  <Form.Label>Intake Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.intake_date || ""}
                    onChange={handleChange("intake_date")}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Guidance note (replaces the former "How can we help today?" textarea) */}
            <div className="mb-3">
              <div className="form-label mb-1">How Can We Help You Today? (removed)</div>
              <div className="text-danger fw-bold">
                NOTE: Complete Partial Intake and add then add Service Request(s).
              </div>
            </div>

            {/* Name */}
            <Row>
              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="firstName">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.first_name || ""}
                    onChange={handleChange("first_name")}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="middleName">
                  <Form.Label>Middle Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.middle_name || ""}
                    onChange={handleChange("middle_name")}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="lastName">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.last_name || ""}
                    onChange={handleChange("last_name")}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Birth Date + Phones */}
            <Row>
              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="birthDate">
                  <Form.Label>Birth Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.birth_date || ""}
                    onChange={handleChange("birth_date")}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="homePhone">
                  <Form.Label>Home Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.phone_primary || ""}
                    onChange={handleChange("phone_primary")}
                    disabled={!!form.no_phone}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="mobilePhone">
                  <Form.Label>Mobile Phone</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.phone_secondary || ""}
                    onChange={handleChange("phone_secondary")}
                    disabled={!!form.no_phone}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* No Phone (satisfies the phone requirement) */}
            <Row>
              <Col md={6} sm={12}>
                <Form.Group className="mb-2" controlId="noPhone">
                  <Form.Check
                    type="checkbox"
                    label="No Phone"
                    checked={!!form.no_phone}
                    onChange={handleCheckbox("no_phone")}
                  />
                  <Form.Text className="text-muted">
                    If checked, the phone fields are disabled and this satisfies the phone requirement for Partial Intake.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Unsheltered (above address) */}
            <Row>
              <Col md={6} sm={12}>
                <Form.Group className="mb-2" controlId="unsheltered">
                  <Form.Check
                    type="checkbox"
                    label="Unsheltered (no fixed address)"
                    checked={!!form.unsheltered}
                    onChange={handleCheckbox("unsheltered")}
                  />
                  <Form.Text className="text-muted">
                    If checked, address fields are disabled and this satisfies the address requirement for Partial Intake.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Address (disabled when unsheltered) */}
            <Row>
              <Col md={5} sm={12}>
                <Form.Group className="mb-3" controlId="address1">
                  <Form.Label>Address Line 1</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.address_line1 || ""}
                    onChange={handleChange("address_line1")}
                    disabled={addressDisabled}
                  />
                </Form.Group>
              </Col>

              <Col md={5} sm={12}>
                <Form.Group className="mb-3" controlId="address2">
                  <Form.Label>Address Line 2</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.address_line2 || ""}
                    onChange={handleChange("address_line2")}
                    disabled={addressDisabled}
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* City/State/ZIP/County */}
            <Row>
              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="city">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.city || ""}
                    onChange={handleChange("city")}
                    disabled={addressDisabled}
                  />
                </Form.Group>
              </Col>

              <Col md={2} sm={3}>
                <Form.Group className="mb-3" controlId="state">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.state || ""}
                    onChange={handleChange("state")}
                    placeholder="MI"
                    disabled={addressDisabled}
                  />
                </Form.Group>
              </Col>

              <Col md={2} sm={3}>
                <Form.Group className="mb-3" controlId="postalCode">
                  <Form.Label>ZIP</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.postal_code || ""}
                    onChange={handleChange("postal_code")}
                    disabled={addressDisabled}
                  />
                </Form.Group>
              </Col>

              <Col md={3} sm={6}>
                <Form.Group className="mb-3" controlId="county">
                  <Form.Label>County</Form.Label>
                  <Form.Select
                    value={form.county || "Clare"}
                    onChange={handleChange("county")}
                    disabled={addressDisabled}
                  >
                    {COUNTY_OPTIONS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* Bottom button: ALWAYS visible; disabled until Partial Intake is satisfied */}
            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="primary"
                onClick={handleDoCompleteIntake}
                disabled={saving || !isPartialIntakeReady}
                title={
                  isPartialIntakeReady
                    ? "Save Partial Intake and continue to Complete Intake."
                    : "Complete the required Partial Intake fields to continue."
                }
              >
                {saving ? "Saving..." : "Do Complete Intake"}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
}
